import { Button } from '@bcecloud/portal-ui';
import { placeholderToken, type OutreachPlaceholder } from '@/lib/outreach-templates';

export interface PlaceholderPickerProps {
  readonly placeholders: readonly OutreachPlaceholder[];
  /** The name of the field an insert lands in, as the rep reads it ("subject", "email"). */
  readonly target: string;
  readonly onInsert: (key: string) => void;
}

/**
 * The placeholders a template may use, as buttons. Each inserts its
 * `{placeholder}` at the cursor in the field the rep was last typing in.
 *
 * @param props - The placeholders, the target field's name and the insert handler.
 * @returns The picker.
 */
export function PlaceholderPicker({ placeholders, target, onInsert }: PlaceholderPickerProps): React.ReactElement {
  return (
    <div role="group" aria-label="Insert a placeholder">
      <p className="text-portal-micro text-portal-muted">
        Insert a placeholder into the {target}. A draft fills each one in from the client’s history.
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        {placeholders.map((placeholder) => (
          <Button
            key={placeholder.key}
            variant="secondary"
            size="compact"
            title={placeholder.label}
            aria-label={`Insert ${placeholder.label}`}
            // Keep the caret where it is: a mousedown would blur the field first.
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => onInsert(placeholder.key)}
          >
            {placeholderToken(placeholder.key)}
          </Button>
        ))}
      </div>
    </div>
  );
}
